
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface RecentAchievement {
  id: string;
  name: string;
  description: string;
  difficulty: string;
  xpReward: number;
  earnedDate: string;
  icon?: string;
}

/**
 * Lightweight hook for NavigationBar - fetches only recent achievements
 * Does NOT use AnalogHorror context to avoid provider dependency issues
 */
export const useRecentAchievements = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['recent-achievements', userId],
    queryFn: async (): Promise<RecentAchievement[]> => {
      if (!userId) return [];
      
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data, error } = await supabase
        .from('user_achievements')
        .select(`
          achievement_id,
          earned_at,
          achievements (
            id,
            name,
            description,
            difficulty,
            xp_reward,
            icon
          )
        `)
        .eq('user_id', userId)
        .gte('earned_at', sevenDaysAgo.toISOString())
        .order('earned_at', { ascending: false })
        .limit(10);
      
      if (error) {
        console.error('Error fetching recent achievements:', error);
        return [];
      }
      
      return (data || []).map((item: any) => ({
        id: item.achievements?.id || item.achievement_id,
        name: item.achievements?.name || 'Achievement',
        description: item.achievements?.description || '',
        difficulty: item.achievements?.difficulty || 'bronze',
        xpReward: item.achievements?.xp_reward || 0,
        earnedDate: item.earned_at,
        icon: item.achievements?.icon
      }));
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - reduces refetches
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
  });
};
